<template>
  <WrappedForm
    ref="form"
    class="question-editor"
    accessible
    label-position="top"
    label-bold
    :hide-required-asterisk="!showRequiredAsterisk"
    :model="questionData"
    :formLines="formLines"
    :validateOnRuleChange="false"
  >
    <template #options="{lineName, line }">
      <div class="options-tip">{{ line.tipMessage }}</div>
      <div class="options-container">
        <div class="options">
          <label v-if="showKnockoutOptions">{{ line.optionsLabel }}</label>
          <el-input
            v-model="questionData[lineName]"
            ref="optionsInput"
            class="options-input"
            type="textarea"
            :autosize="{ minRows: 5}"
            :name="lineName"
            :placeholder="line.placeholder"
            @blur="onOptionsInputBlur"
          />
        </div>
        <div v-if="showKnockoutOptions && lineName === 'options'" class="options knockout">
          <label>{{ $t(`${i18nPrefix}.label.markAsKnockoutOptions`) }}</label>
          <el-select
            v-model="questionData.knockoutOptions"
            multiple
            ref="knockoutSelect"
            class="knockout-options-select"
            name="knockoutOptions"
            :placeholder="$t(`${i18nPrefix}.placeholder.markAsKnockoutOptions`)"
            @change="handleKnockoutChange"
          >
            <el-option v-for="(item) in questionOptionList" :key="item._vid" :label="item.label" :value="item._vid"></el-option>
          </el-select>
        </div>
      </div>
    </template>
    <template #groupSelect="{ lineName, line }">
      <el-select
        v-model="questionData[lineName]"
        :name="lineName"
        :disabled="line.disabled"
        :placeholder="line.placeholder"
      >
        <el-option
          v-for="(option, index) in line.option.data"
          :key="index"
          :label="option.i18n_name"
          :value="option.id"
        />
      </el-select>
    </template>
  </WrappedForm>
</template>

<script>
import _ from 'lodash'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import { QUESTION_TYPE, HAS_OPTIONS_QUESTION_TYPES, VIDEO_DURATION, VISIBILITY_TYPE, HAS_KNOCKOUT_OPTIONS_QUESTION_TYPES } from '@/constant/openQuestion'
import { createI18nProperty } from '@/i18n'
import { i18nPrefix } from './common'
import { hasOptionChild, reconcileOpenQuestionOptions } from '@/utils/openQuestionTree'

export const visibilityOptionList = _.map(VISIBILITY_TYPE, (value, typeName) => {
  const option = { value, typeName }
  createI18nProperty(option, 'label', `${i18nPrefix}.visibilityType.${typeName}`)
  return option
})

export const questionTypeOptionList = _.map(QUESTION_TYPE, (value, typeName) => {
  const option = { value, typeName }
  createI18nProperty(option, 'label', `${i18nPrefix}.questionType.${typeName}`)
  return option
})

export default {
  components: {
    WrappedForm
  },
  props: {
    isTemplate: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      required: true
    },
    groupList: {
      type: Array,
      default: null
    },
    question: {
      required: false
    },
    disabledQuestionType: {
      type: Array,
      default: () => []
    },
    hasVisibilityOption: {
      type: Boolean,
      default: false
    },
    defaultVisibility: {
      type: String,
      default: VISIBILITY_TYPE.IE
    },
    showUpdateButton: {
      type: Boolean,
      default: false
    },
    questionTypeLabel: {
      type: String,
      default: ''
    },
    templateName: {
      type: String,
      default: ''
    },
    // 新增：可选题目列表（用于子题选择）
    questionList: {
      type: Array,
      default: () => []
    },
    // 新增：当前题目索引（排除自己）
    currentQuestionIndex: {
      type: Number,
      default: -1
    },
    // Opt-in to preserve the legacy embedded editors' current appearance.
    showRequiredAsterisk: {
      type: Boolean,
      default: false
    },
    disabledTemplate: {
      type: Boolean,
      default: false
    },
    disableQuestionType: {
      type: Boolean,
      default: false
    },
    relationFields: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      i18nPrefix,
      QUESTION_TYPE,
      questionData: {
        id: null,
        groupId: -1,
        type: QUESTION_TYPE.TEXT,
        title: null,
        required: false,
        hasKnockout: false,
        options: null,
        knockoutOptions: null,
        videoDuration: null,
        updateButton: false,
        parentQuestion: null,
        triggerOption: null
      },
      questionOptionList: [],
      originalLinkedOptionKeys: [],
      ambiguousLinkedOptionChange: false
    }
  },
  computed: {
    form () {
      return this.$refs.form
    },
    questionWatcher () {
      return this.question
    },
    questionTypeList () {
      return _.filter(questionTypeOptionList, ({ value }) => !this.disabledQuestionType.includes(value))
    },
    defaultQuestionType () {
      return this.questionTypeList[0].value
    },
    formLines () {
      const vm = this
      const questionData = this.questionData
      const requiredMessage = this.$t('common.message.requiredFormField')
      const relationFields = this.relationFields || {}
      const parentQuestion = relationFields.parentQuestion
      const triggerOption = relationFields.triggerOption
      const lines = {
        templateName: {
          hidden: !this.templateName,
          disabled: true,
          label: this.$t(`${this.i18nPrefix}.label.group`)
        },
        groupId: {
          elType: 'el-select',
          contentSlot: 'groupSelect',
          hidden: !this.isTemplate,
          label: this.$t(`${this.i18nPrefix}.label.group`),
          disabled: this.disabledTemplate,
          option: {
            data: this.groupList,
            labelKey: 'i18n_name',
            valueKey: 'id'
          }
        },
        parentQuestion: {
          elType: 'el-input',
          hidden: !parentQuestion,
          disabled: true,
          class: 'relation-field-line relation-parent-question',
          label: this.$t(`${this.i18nPrefix}.label.parentQuestion`)
        },
        triggerOption: {
          elType: triggerOption && triggerOption.control === 'select' ? 'el-select' : 'el-input',
          hidden: !triggerOption,
          disabled: triggerOption && triggerOption.disabled,
          class: 'relation-field-line relation-trigger-option',
          label: this.$t(`${this.i18nPrefix}.label.triggerOption`),
          required: Boolean(triggerOption && triggerOption.required),
          props: triggerOption && triggerOption.control === 'select' && triggerOption.filterable ? { filterable: true } : {},
          option: {
            data: _.cloneDeep((triggerOption && triggerOption.options) || []),
            labelKey: 'label',
            valueKey: 'key'
          },
          events: triggerOption && triggerOption.control === 'select'
            ? { change: value => this.$emit('relation-trigger-option-change', value) }
            : null
        },
        type: {
          elType: 'el-select',
          disabled: this.disableQuestionType || this.questionTypeList.length === 1,
          label: this.questionTypeLabel || this.$t(`${this.i18nPrefix}.label.questionType`),
          option: {
            data: this.questionTypeList
          }
        },
        title: {
          label: this.$t(`${this.i18nPrefix}.label.questionTitle`),
          rules: [
            { required: true, message: requiredMessage, trigger: 'blur' }
          ],
          props: {
            type: 'textarea',
            autosize: {
              minRows: 4
            }
          }
        },
        options: {
          get hidden () {
            return !HAS_OPTIONS_QUESTION_TYPES.includes(questionData.type)
          },
          label: this.$t(`${this.i18nPrefix}.label.options`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.options`),
          contentSlot: 'options',
          optionsLabel: this.$t(`${this.i18nPrefix}.label.allOptions`),
          get tipMessage () {
            let tip = vm.$t(`${vm.i18nPrefix}.message.notKnockoutOptionsTip`)
            if (questionData.hasKnockout) {
              tip += ' ' + vm.$t(`${vm.i18nPrefix}.message.knockoutOptionsTip`)
            }
            return tip
          },
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        required: {
          elType: 'el-checkbox',
          class: 'required-checkbox-line',
          checkboxLabel: this.$t(`${this.i18nPrefix}.label.requiredQuestion`)
        },
        hasKnockout: {
          elType: 'el-checkbox',
          class: 'knockout-checkbox-line',
          get hidden () {
            return !HAS_KNOCKOUT_OPTIONS_QUESTION_TYPES.includes(questionData.type)
          },
          checkboxLabel: this.$t(`${this.i18nPrefix}.label.includeKnockoutOptions`)
        },
        visibility: {
          elType: 'el-select',
          hidden: !this.hasVisibilityOption,
          label: this.$t(`${this.i18nPrefix}.label.visibility`),
          option: {
            data: visibilityOptionList
          },
          rules: [
            { required: true, message: requiredMessage, trigger: 'blur' }
          ]
        },
        videoDuration: {
          elType: 'el-radio-group',
          get hidden () {
            return questionData.type !== QUESTION_TYPE.VIDEO
          },
          label: this.$t(`${this.i18nPrefix}.label.duration`),
          option: {
            data: _.map(VIDEO_DURATION, (value) => {
              return { label: value, value }
            })
          }
        },
        updateButton: {
          elType: 'el-checkbox',
          get hidden () {
            return !vm.isEdit || !vm.showUpdateButton
          },
          checkboxLabel: this.$t(`${this.i18nPrefix}.label.createUpdateTask`)
        }
      }
      return lines
    },
    showKnockoutOptions () {
      const questionData = this.questionData
      return HAS_KNOCKOUT_OPTIONS_QUESTION_TYPES.includes(questionData.type) && questionData.hasKnockout
    }
  },
  created () {

  },
  methods: {
    // 处理淘汰项选择变更，选择后自动关闭下拉浮层
    handleKnockoutChange () {
      this.$nextTick(() => {
        if (this.$refs.knockoutSelect) {
          this.$refs.knockoutSelect.blur()
        }
      })
    },
    parse (questionData) {
      if (!questionData) {
        questionData = this.questionData
      }
      const options = []
      const knockoutOptions = questionData.knockoutOptions || []
      _.forEach(this.questionOptionList, (option, index) => {
        const vid = option._vid
        options.push({
          ...(option.sourceOption || {}),
          description: option.value,
          is_knockout: knockoutOptions.includes(vid) ? 1 : 0,
          order: index + 1
        })
      })
      const question = {
        id: questionData.id,
        group_id: questionData.groupId,
        type: questionData.type,
        title: questionData.title,
        required: questionData.required,
        options,
        duration: questionData.videoDuration,
        need_update: questionData.updateButton || false
      }
      if (this.hasVisibilityOption) {
        question.visibility = questionData.visibility
      }
      if (questionData.type !== QUESTION_TYPE.VIDEO) {
        question.duration = null
      }
      if (!HAS_OPTIONS_QUESTION_TYPES.includes(questionData.type)) {
        question.options = null
      }
      return question
    },
    getRelationFieldValue (name) {
      const field = this.relationFields && this.relationFields[name]
      return field ? field.value : null
    },
    toData (question) {
      if (question && Object.keys(question).length > 0) {
        let hasKnockout = false
        const options = []
        const knockoutOptions = []
        const questionOptionList = []
        _.forEach(question.options, (item, index) => {
          const description = item.description || ''
          const vid = item.id ? `id_${item.id}` : item._vid ? `vid_${item._vid}` : `${index}_${description}`
          const sourceOption = _.cloneDeep(_.omit(item, ['child_question', '_treeChildTitle', 'description']))
          if (item.child_question && !sourceOption.child_question_id && !sourceOption.child_question_vid) {
            if (item.child_question.id) {
              sourceOption.child_question_id = item.child_question.id
              sourceOption.next_question_id = item.child_question.id
            } else if (item.child_question._vid) {
              sourceOption.child_question_vid = item.child_question._vid
              sourceOption.next_question_id = null
            }
          }
          if (item.is_knockout === 1) {
            hasKnockout = true
            knockoutOptions.push(vid)
          }
          questionOptionList.push({
            _vid: vid,
            index: index,
            label: description,
            value: description,
            sourceOption
          })
          options.push(description)
        })
        this.questionOptionList = questionOptionList
        this.ambiguousLinkedOptionChange = false
        this.originalLinkedOptionKeys = _.chain(questionOptionList)
          .filter(option => hasOptionChild(option.sourceOption))
          .map('_vid')
          .value()
        return {
          id: question.id,
          templateName: this.templateName,
          groupId: question.group_id,
          type: question.type || this.defaultQuestionType,
          title: question.title || '',
          required: question.required,
          hasKnockout,
          options: options.join('\n'),
          knockoutOptions: knockoutOptions,
          videoDuration: question.duration || VIDEO_DURATION.S30,
          visibility: this.hasVisibilityOption ? question.visibility || this.defaultVisibility : undefined,
          updateButton: false,
          parentQuestion: this.getRelationFieldValue('parentQuestion'),
          triggerOption: this.getRelationFieldValue('triggerOption')
        }
      } else {
        this.questionOptionList = []
        this.ambiguousLinkedOptionChange = false
        this.originalLinkedOptionKeys = []
        return {
          id: null,
          templateName: this.templateName,
          groupId: -1,
          type: this.defaultQuestionType,
          title: null,
          required: false,
          hasKnockout: false,
          options: null,
          knockoutOptions: [],
          videoDuration: VIDEO_DURATION.S30,
          visibility: this.hasVisibilityOption ? VISIBILITY_TYPE.IE : undefined,
          updateButton: false,
          parentQuestion: this.getRelationFieldValue('parentQuestion'),
          triggerOption: this.getRelationFieldValue('triggerOption')
        }
      }
    },
    clearData () {
      this.questionData = this.toData(null)
      this.$nextTick(() => this.clearValidate())
    },
    clearValidate () {
      this.form.clearValidate()
    },
    async submit () {
      this.syncQuestionOptionList()
      const isValid = await this.form.submit()
      if (!isValid) {
        return null
      } else if (this.hasRemovedLinkedOption()) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.removeLinkedOptionFirst`))
        return null
      } else {
        return this.parse()
      }
    },
    hasRemovedLinkedOption () {
      if (!HAS_OPTIONS_QUESTION_TYPES.includes(this.questionData.type) && this.originalLinkedOptionKeys.length) {
        return true
      }
      const retainedKeys = _.chain(this.questionOptionList)
        .filter('sourceOption')
        .map('_vid')
        .value()
      return this.ambiguousLinkedOptionChange || _.some(this.originalLinkedOptionKeys, key => !retainedKeys.includes(key))
    },
    getOptionValues (value) {
      const normalized = (value || '').replace(/\\n/g, '\n').trim()
      return normalized ? normalized.split('\n').map(item => item.trim()).filter(item => item) : []
    },
    syncQuestionOptionList () {
      const questionData = this.questionData
      const newVIds = []
      const optionValues = this.getOptionValues(questionData.options)
      const previousOptions = this.questionOptionList || []
      const result = reconcileOpenQuestionOptions({
        previousOptions,
        optionValues,
        createOptionKey: index => `new_${Date.now()}_${index}`,
        isLinkedOption: option => hasOptionChild(option.sourceOption)
      })
      const allOptions = result.options
      this.ambiguousLinkedOptionChange = result.ambiguousLinkedChange
      allOptions.forEach(option => newVIds.push(option._vid))
      const knockoutOptions = questionData.knockoutOptions
      if (knockoutOptions && knockoutOptions.length > 0) {
        questionData.knockoutOptions = _.filter(knockoutOptions, item => newVIds.includes(item))
      }
      this.questionOptionList = allOptions
    },
    onOptionsInputBlur () {
      this.syncQuestionOptionList()
    }
  },
  watch: {
    questionWatcher: {
      handler () {
        this.questionData = this.toData(this.question)
        this.$nextTick(() => this.clearValidate())
      },
      immediate: true
    },
    'questionData.type' () {
      this.$nextTick(() => this.clearValidate())
    },
    templateName (value) {
      this.$set(this.questionData, 'templateName', value)
    },
    relationFields: {
      handler () {
        this.$set(this.questionData, 'parentQuestion', this.getRelationFieldValue('parentQuestion'))
        this.$set(this.questionData, 'triggerOption', this.getRelationFieldValue('triggerOption'))
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.question-editor {
  color: #303133;

  ::v-deep .el-form-item__label,
  ::v-deep .label-inner {
    color: #303133;
  }

  ::v-deep .el-input__inner,
  ::v-deep .el-textarea__inner {
    color: #303133;
    border-color: #DCDFE6;

    &::placeholder {
      color: #C0C4CC;
    }
  }

  ::v-deep .required-checkbox-line {
    margin-top: -36px;
  }
  ::v-deep .knockout-checkbox-line {
    margin-top: -48px;
  }

  ::v-deep .relation-trigger-option.is-required:not(.is-no-asterisk) > .el-form-item__label {
    &::before {
      display: none;
    }

    &::after {
      content: '*';
      margin: 9px 0 0 3px;
      color: #f56c6c;
      font-weight: bold;
      line-height: 1;
    }
  }

  ::v-deep .relation-field-line .el-input.is-disabled .el-input__inner {
    color: #606266;
    border-color: #E4E7ED;
    background: #F5F7FA;
    -webkit-text-fill-color: #606266;
  }

  .options-tip {
    color: #606266;
    line-height: initial;
    white-space: break-spaces;
    word-break: keep-all;
  }

  .options-container {
    display: flex;

    .options {
      flex: 1;
      display: flex;
      flex-direction: column;

      &.knockout {
        margin-left: 20px;
      }

      .knockout-options-select {
        flex: 1;
        border: 1px solid #DCDFE6;
        cursor: pointer;

        ::v-deep {
          .el-select__tags {
            top: 0;
            transform: none;
          }

          .el-input {
            height: 100%;

            .el-input__inner {
              border: 0
            }

            .el-input__suffix {
              display: none;
            }
          }
        }
      }
    }
  }
}

.el-select-dropdown__item {
  min-width: 100%;
  max-width: 40vw;
  overflow: hidden;
  word-break: keep-all;
  text-overflow: ellipsis;
}
</style>
